import { Button, Dialog } from '@alifd/next';
import { Select, Space, Table, TableColumnsType, TableProps, Tabs } from 'antd';
import moment from 'moment';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import SignatureCanvas from 'react-signature-canvas';
import Webcam from 'react-webcam';
import Swal from 'sweetalert2';

import ezconImg from '@/../public/ezcon.png';
import type { Query } from '@/lib/sendMessage';
import sendNotification from '@/lib/sendMessage';
import type { RootState } from '@/redux/store';
import type { DepositRegistration } from '@/types/database/depositRegistration';
import type { User } from '@/types/database/users';

import styles from './index.module.scss';
import { getDetailList, sendInfo } from './utils';

const DepositItemsTable: React.FC<{
  data: DepositRegistration[];
  updateData: () => void;
  dataClient: any;
  currentUser: User | null;
}> = ({ data, updateData, dataClient, currentUser }) => {
  // UI state
  const [currentPage, setCurrentPage] = useState<
    'fromCommunity' | 'fromOutside' | 'received'
  >('fromCommunity');
  const [showDialog, setShowDialog] = useState(false);
  const [dialogType, setDialogType] = useState<
    'receive' | 'detail' | 'webcam' | 'signBoard'
  >('receive');
  const [toBeReceivedItems, setToBeReceivedItems] = useState<{
    fromCommunity: (DepositRegistration & { key: string })[];
    fromOutside: (DepositRegistration & { key: string })[];
  }>({
    fromCommunity: [],
    fromOutside: [],
  });
  const [receivedItems, setReceivedItems] = useState<
    (DepositRegistration & { key: string })[]
  >([]);
  const [updateItems, setUpdateItems] = useState<DepositRegistration[]>([]);

  const [showDetailItem, setShowDetailItem] =
    useState<DepositRegistration | null>(null);
  const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedVideoDevice, setSelectedVideoDevice] = useState<string | null>(
    null
  );
  const [receiverPhoto, setReceiverPhoto] = useState<string | null>(null);
  const [receiverSign, setReceiverSign] = useState<string | null>(null);

  const webcamRef = useRef<Webcam>(null);
  const signatureRef = useRef<SignatureCanvas>(null);

  const user = useSelector((state: RootState) => state.user);

  const handleShowDetail = useCallback((item: DepositRegistration) => {
    setShowDialog(true);
    setDialogType('detail');
    setShowDetailItem(item);
  }, []);

  const handleInfo = useCallback(
    async (id: string) => {
      const item = data.find((item) => item._id === id);
      if (item) {
        try {
          await sendInfo(item);
          Swal.fire({
            title: '通知成功',
            icon: 'success',
            showConfirmButton: false,
            timer: 1500,
          });
        } catch (error) {
          console.error(error);
          Swal.fire({
            title: '通知失敗',
            icon: 'error',
            showConfirmButton: false,
            timer: 1500,
          });
        }
      } else {
        Swal.fire({
          title: '找不到該物品',
          icon: 'error',
          showConfirmButton: false,
          timer: 1500,
        });
      }
    },
    [data]
  );

  const handleMultiInfo = useCallback(async () => {
    try {
      await Promise.all(updateItems.map((item) => sendInfo(item)));
      Swal.fire({
        title: '通知成功',
        icon: 'success',
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (error) {
      console.error(error);
      Swal.fire({
        title: '通知失敗',
        icon: 'error',
        showConfirmButton: false,
        timer: 1500,
      });
    }
  }, [updateItems]);

  const handlePrepareOneReceive = useCallback((item: DepositRegistration) => {
    setUpdateItems([item]);
    setShowDialog(true);
    setDialogType('receive');
  }, []);

  const handlePrepareMultiReceive = useCallback(async () => {
    if (updateItems.length === 0) {
      Swal.fire({
        title: '請選擇項目',
        icon: 'warning',
        showConfirmButton: false,
        timer: 1500,
      });
      return;
    }
    const isSelectingMultipleHouse =
      [...new Set(updateItems.map((item) => item.household))].length > 1;
    if (isSelectingMultipleHouse) {
      const { isConfirmed } = await Swal.fire({
        title: '您選擇的郵件包裹不屬於同一戶，是否繼續？',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: '確定',
        cancelButtonText: '取消',
        confirmButtonColor: '#209BFA',
        cancelButtonColor: '#999',
      });
      if (!isConfirmed) {
        return;
      }
    }
    setShowDialog(true);
    setDialogType('receive');
  }, [updateItems]);

  const handleReceiveConfirm = useCallback(async () => {
    if (receiverPhoto || receiverSign) {
      await Promise.all(
        updateItems.map(async (item) => {
          const updateData = {
            status: '已領取',
            residentPhoto: receiverPhoto,
            canvas: receiverSign,
            receiveTime: moment().format('YYYY-MM-DD HH:mm:ss'),
          };
          await dataClient.patch(item._id, updateData);
        })
      );
      const receiverName = currentUser?.name || '住戶';
      const method = receiverSign ? '簽名' : '拍照';
      const adminName = user?.auth?.user?.name || '';
      updateItems.forEach((item) => {
        const message = `${receiverName}以${method}方式向管理員：${adminName}領取${item.household}${item.type}`;
        let query: Query;
        if (currentUser?._id) {
          query = {
            _id: currentUser._id,
            firebaseClass: 'depositRegistration',
          };
        } else {
          query = {
            householdNumbers: item.household,
            firebaseClass: 'depositRegistration',
          };
        }
        sendNotification({
          title: '寄物領取通知',
          content: message,
          class: 'deposit',
          redirect: '/mobile/deposit/depositFrontDesk',
          query,
        });
      });
      updateData();
      setShowDialog(false);
      setUpdateItems([]);
      setReceiverPhoto(null);
      setReceiverSign(null);
      Swal.fire({
        title: '領取成功',
        icon: 'success',
        showConfirmButton: false,
        timer: 1500,
      });
    } else {
      Swal.fire({
        title: '請拍照或簽名',
        icon: 'warning',
        showConfirmButton: false,
        timer: 1500,
      });
      return;
    }
  }, [
    currentUser?._id,
    currentUser?.name,
    dataClient,
    receiverPhoto,
    receiverSign,
    updateData,
    updateItems,
    user?.auth?.user?.name,
  ]);

  const handleCapture = useCallback(() => {
    if (webcamRef.current) {
      const imageSrc = webcamRef.current.getScreenshot();
      setReceiverPhoto(imageSrc);
    }
  }, [webcamRef]);

  const handleClearSignature = useCallback(() => {
    if (signatureRef.current) {
      signatureRef.current.clear();
    }
  }, [signatureRef]);

  const handleSaveSignature = useCallback(() => {
    if (signatureRef.current) {
      const imageSrc = signatureRef.current.getTrimmedCanvas().toDataURL();
      setReceiverSign(imageSrc);
      setDialogType('receive');
    }
  }, [signatureRef]);

  const handleCancelReceiveProcess = useCallback(() => {
    setShowDialog(false);
    setReceiverPhoto(null);
    setReceiverSign(null);
  }, []);

  const tableRowSelection: TableProps<DepositRegistration>['rowSelection'] =
    useMemo(
      () => ({
        type: 'checkbox',
        selectedRowKeys: updateItems.map((item) => item._id || Math.random()),
        onChange(_, selectedRows) {
          setUpdateItems(selectedRows);
        },
      }),
      [updateItems]
    );

  const receiveTimeSorter = (
    a: DepositRegistration,
    b: DepositRegistration
  ) => {
    const receiveTimeA = a.receiveTime as number | '待領取';
    const receiveTimeB = b.receiveTime as number | '待領取';

    if (typeof receiveTimeA !== 'number') {
      return receiveTimeA.localeCompare(receiveTimeB.toString());
    }
    if (typeof receiveTimeB !== 'number') {
      return receiveTimeA.toString().localeCompare(receiveTimeB);
    }
    return receiveTimeA - receiveTimeB;
  };
  const columns = useMemo(() => {
    const columns: TableColumnsType<DepositRegistration> = [
      {
        title: '編號',
        dataIndex: 'plateNumber',
        key: 'plateNumber',
        sorter: (a, b) => a.plateNumber - b.plateNumber,
      },
      {
        title: '包裹類型',
        dataIndex: 'type',
        key: 'type',
        sorter: (a, b) => a.type.localeCompare(b.type),
      },
      {
        title: '包裹名稱',
        dataIndex: 'itemName',
        key: 'itemName',
        sorter: (a, b) => a.itemName.localeCompare(b.itemName),
      },
      {
        title: currentPage === 'fromOutside' ? '收件人戶號' : '寄件人戶號',
        dataIndex: 'household',
        key: 'household',
        sorter: (a, b) => {
          const peopleHouseholdA = a.peopleHousehold || '';
          const peopleHouseholdB = b.peopleHousehold || '';
          return a.type === '寄件'
            ? a.household.localeCompare(b.household)
            : peopleHouseholdA.localeCompare(peopleHouseholdB);
        },
        render: (_value, record) => {
          if (currentPage === 'received') {
            return record.type === '寄件'
              ? record.household
              : record.peopleHousehold;
          }
          return record.household;
        },
      },
      {
        title: currentPage === 'fromOutside' ? '寄件人' : '收件人',
        dataIndex: 'peopleName',
        key: 'peopleName',
        sorter: (a, b) => {
          const recipientA = a.recipient;
          const recipientB = b.recipient;
          return a.type === '寄件'
            ? a.peopleName.localeCompare(b.peopleName)
            : recipientA.localeCompare(recipientB);
        },
        render: (_value, record) => {
          if (currentPage === 'received') {
            return record.type === '寄件'
              ? record.peopleName
              : record.recipient;
          }
          return record.peopleName;
        },
      },
      {
        title: '狀態',
        dataIndex: 'status',
        key: 'status',
      },
      {
        title: '登記時間',
        dataIndex: 'packagerRegistrationTime',
        sorter: (a, b) =>
          a.packagerRegistrationTime - b.packagerRegistrationTime,
        render: (value) => moment(value).format('YYYY-MM-DD HH:mm:ss'),
      },
    ];
    if (currentPage === 'received') {
      columns.splice(
        4,
        0,
        {
          title: '寄件人',
          dataIndex: 'recipient',
          key: 'recipient',
          sorter: (a, b) =>
            a.type === '寄件'
              ? a.recipient.localeCompare(b.recipient)
              : a.peopleName.localeCompare(b.peopleName),
          render: (_value, record) => {
            return (
              (record.type === '寄件' ? record.recipient : record.peopleName) ||
              ''
            );
          },
        },
        {
          title: '收件人戶號',
          dataIndex: 'peopleHousehold',
          key: 'peopleHousehold',
          sorter: (a, b) => {
            const peopleHouseholdA =
              (a.type === '寄件' ? a.peopleHousehold : a.household) || '';
            const peopleHouseholdB =
              (a.type === '寄件' ? b.peopleHousehold : b.household) || '';
            return peopleHouseholdA.localeCompare(peopleHouseholdB);
          },
          render: (_value, record) => {
            return (
              (record.type === '寄件'
                ? record.peopleHousehold
                : record.household) || ''
            );
          },
        }
      );
    }
    const timeColumn: TableColumnsType<DepositRegistration>[0] = {
      dataIndex: 'receiveTime',
      key: 'receiveTime',
      sorter: (a, b) => receiveTimeSorter(a, b),
    };
    if (currentPage !== 'received') {
      timeColumn.title = '未領取時間';
    } else {
      timeColumn.title = '領取時間';
    }
    columns.push(timeColumn);
    columns.push({
      title: '操作',
      dataIndex: '_id',
      fixed: 'right',
      key: '_id',
      render: (id, depositItem) => {
        return (
          <Space>
            <Button onClick={() => handleShowDetail(depositItem)}>查看</Button>
            {currentPage !== 'received' && (
              <>
                <Button
                  type="secondary"
                  onClick={() => handlePrepareOneReceive(depositItem)}
                >
                  領取
                </Button>
                <Button type="primary" onClick={() => handleInfo(id)}>
                  通知住戶
                </Button>
              </>
            )}
          </Space>
        );
      },
    });
    return columns;
  }, [currentPage, handleInfo, handlePrepareOneReceive, handleShowDetail]);

  useEffect(() => {
    const getVideoDevices = async () => {
      if (navigator?.mediaDevices?.enumerateDevices) {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter(
          (device) => device.kind === 'videoinput'
        );
        setVideoDevices(videoDevices);
        setSelectedVideoDevice(videoDevices?.[0]?.deviceId || null);
      }
    };
    getVideoDevices();
  }, []);

  useEffect(() => {
    setUpdateItems([]);
    const newData = data.map((data) => ({
      ...data,
      key: data._id,
    }));
    setReceivedItems(newData.filter((data) => data.status === '已領取'));
    setToBeReceivedItems(
      newData.reduce(
        (acc, data) => {
          if (data.status !== '已領取') {
            switch (data.type) {
              case '寄件':
                acc.fromCommunity.push(data);
                break;
              case '收件':
                acc.fromOutside.push(data);
                break;
            }
          }
          return acc;
        },
        { fromCommunity: [], fromOutside: [] } as typeof toBeReceivedItems
      )
    );
  }, [data, currentPage]);

  return (
    <Space
      direction="vertical"
      style={{
        width: '100%',
      }}
    >
      <Tabs
        activeKey={currentPage}
        onChange={(tab) => {
          setCurrentPage(tab as 'fromCommunity' | 'fromOutside' | 'received');
          setUpdateItems([]);
        }}
        style={{
          width: '100%',
        }}
        items={[
          {
            key: 'fromCommunity',
            label: `寄件(${toBeReceivedItems.fromCommunity.length})`,
          },
          {
            key: 'fromOutside',
            label: `收件(${toBeReceivedItems.fromOutside.length})`,
          },
          {
            key: 'received',
            label: `已領取(${receivedItems.length})`,
          },
        ]}
      />
      {currentPage !== 'received' && (
        <Space>
          <Button type="primary" onClick={handleMultiInfo}>
            一鍵通知
          </Button>
          <Button type="secondary" onClick={handlePrepareMultiReceive}>
            一鍵領取
          </Button>
        </Space>
      )}
      {
        <Table
          scroll={{ x: 'max-content' }}
          bordered
          rowSelection={tableRowSelection}
          dataSource={
            currentPage === 'received'
              ? receivedItems
              : toBeReceivedItems[currentPage]
          }
          columns={columns}
          sortDirections={['descend', 'ascend', 'descend']}
          showSorterTooltip={false}
          style={{
            width: '100%',
          }}
        />
      }
      <Dialog
        // 根據內容改變
        title="包裹詳細資訊"
        visible={showDialog}
        onClose={handleCancelReceiveProcess}
        footer={false}
      >
        {dialogType === 'detail' && (
          <>
            {showDetailItem && (
              <div className={styles.detailContainer}>
                {getDetailList(showDetailItem, currentPage).map((item) => (
                  <div key={item.label} className={styles.detail}>
                    <span>{item.label}</span>
                    <div>{item.content}</div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
        {dialogType !== 'detail' && (
          <div className={styles.dialogContainer}>
            {dialogType === 'receive' && (
              <div className={styles.receiveFunctionsContainer}>
                <div className={styles.receiveFuncContainer}>
                  <p className={styles.title}>領取人拍照</p>
                  <div className={styles.receiveFuncPreview}>
                    <div className={styles.centerContainer}>
                      <img src={receiverPhoto || ezconImg} />
                    </div>
                  </div>
                  <Button onClick={() => setDialogType('webcam')}>
                    開啟拍照
                  </Button>
                </div>
                <div className={styles.receiveFuncContainer}>
                  <p className={styles.title}>領取人簽名</p>
                  <div className={styles.receiveFuncPreview}>
                    <div className={styles.centerContainer}>
                      <img src={receiverSign || ezconImg} />
                    </div>
                  </div>
                  <Button onClick={() => setDialogType('signBoard')}>
                    開啟簽名版
                  </Button>
                </div>
                <div className={styles.receiveButtons}>
                  <Button
                    disabled={!(receiverPhoto || receiverSign)}
                    type="primary"
                    onClick={handleReceiveConfirm}
                  >
                    確定
                  </Button>
                  <Button onClick={handleCancelReceiveProcess}>取消</Button>
                </div>
              </div>
            )}
            {dialogType === 'webcam' && (
              <div className={styles.webcamFuncContainer}>
                <div className={styles.webcamSection}>
                  <p className={styles.title}>鏡頭</p>
                  <div className={styles.webcamContainer}>
                    <Webcam
                      className={styles.webcam}
                      audio={false}
                      mirrored
                      ref={webcamRef}
                      screenshotFormat="image/jpeg"
                      videoConstraints={{
                        deviceId: selectedVideoDevice ?? undefined,
                        width: 640,
                        height: 640,
                      }}
                    />
                  </div>
                </div>
                <div className={styles.webcamSection}>
                  <p className={styles.title}>預覽</p>
                  {receiverPhoto && <img src={receiverPhoto} />}
                </div>
                <div className={styles.webcamButtons}>
                  <Select
                    value={selectedVideoDevice}
                    options={videoDevices.map((device) => ({
                      label: device.label,
                      value: device.deviceId,
                    }))}
                    placeholder="請選擇鏡頭"
                    style={{
                      width: 200,
                    }}
                  />
                  <Button type="primary" onClick={handleCapture}>
                    拍照
                  </Button>
                  <Button onClick={() => setDialogType('receive')}>確定</Button>
                </div>
              </div>
            )}
            {dialogType === 'signBoard' && (
              <div className={styles.signBoardFuncContainer}>
                <p className={styles.title}>簽名區</p>
                <div className={styles.signBoardContainer}>
                  <SignatureCanvas
                    ref={signatureRef}
                    canvasProps={{
                      width: 1000,
                      height: 550,
                    }}
                  />
                </div>
                <div className={styles.signBoardButtons}>
                  <Button onClick={handleClearSignature}>清除</Button>
                  <Button onClick={handleSaveSignature}>確定</Button>
                </div>
              </div>
            )}
          </div>
        )}
      </Dialog>
    </Space>
  );
};

export default DepositItemsTable;
